import { CardTitle } from "@/modern/components/ui/card";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/modern/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/modern/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { cn } from "@/modern/utils";
import { LOCATION_GEO_BUTTON_CONFIG } from "@/other/constants/AddressGeoButtonConfig.jsx";
import { geoLocationButtonUrlHelper } from "@/other/utils/utils.jsx";
import { DashboardCard } from "@/modern/components/dashboard-card";
import { MoreMenu } from "@/modern/components/more-menu";
import { IFormattedLocation, IUseSwivuTaskLocationReturn, useSwivuTaskLocation } from "@/react/hooks/entityHooks/useSwivuTaskLocation";
import { ArrowRight, Loader2, MapPin, Plus, X } from "lucide-react";
import { ReactNode, useCallback, useState } from "react";
import { GOOGLE_STATIC_MAP_API_KEY } from "@/googlePlacesIntegration";
import { ButtonGroup } from "@/modern/components/ui/button-group";
import { useEntityManagerContext } from "@/react/components/entityManager/context/EntityManagerContext";
import { Button } from "@/modern/components/ui/button-shadcn";

export const EntityLocationView = () => {
  const { record } = useEntityManagerContext();
  const taskLocation = useSwivuTaskLocation({ record });
  const { formattedLocation } = taskLocation;
  const showMap = formattedLocation.isResolved && formattedLocation.lat && formattedLocation.lon;
  const [isSelectingLocation, setIsSelectingLocation] = useState(false);

  return (
    <DashboardCard.Root className="h-[30rem]">
      <DashboardCard.Header className="flex items-center justify-between">
        <>
          <CardTitle className="text-base">Location</CardTitle>
          <Command className={`w-auto bg-transparent dark:bg-transparent h-auto transition-opacity duration-300 ${showMap ? "opacity-100" : "opacity-0"}`}>
            <AddressMenu formattedLocation={formattedLocation} />
          </Command>
        </>
      </DashboardCard.Header>
      <DashboardCard.Content className="overflow-hidden flex flex-col min-h-0 items-center gap-4">
        <div className="flex justify-center items-center flex-col gap-4 rounded-md w-full flex-1">
          {showMap ? (
            <div className="rounded-md overflow-hidden h-80 w-full">
              <img
                src={`https://maps.googleapis.com/maps/api/staticmap?center=${formattedLocation.lat},${formattedLocation.lon}&zoom=15&size=800x600&maptype=roadmap&markers=color:red%7C${formattedLocation.lat},${formattedLocation.lon}&key=${GOOGLE_STATIC_MAP_API_KEY}`}
                alt="Map"
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className="h-full w-full flex items-center justify-center  py-4">
              <div className="h-full w-full flex flex-col items-center gap-6 rounded-2xl border border-dashed border-muted-foreground/30 bg-muted/40 px-8 py-8 text-center shadow-sm justify-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <MapPin className="h-6 w-6" />
                </span>
                <div className="space-y-2">
                  <h3 className="text-base font-semibold text-foreground">No location set</h3>
                  <p className="text-sm text-muted-foreground">Add a location to see it on the map and get directions.</p>
                </div>
                <AddressAutoComplete clearable={false} {...taskLocation} isOpen={isSelectingLocation} onOpenChange={setIsSelectingLocation}>
                  <Button className="inline-flex items-center gap-2">
                    <Plus className="size-4" />
                    Select location
                  </Button>
                </AddressAutoComplete>
              </div>
            </div>
          )}
        </div>
        {showMap && (
          <AddressAutoComplete {...taskLocation} className="mt-auto flex-shrink-0 pb-6">
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex-1 flex items-center gap-2 justify-start">
                  <MapPin className={cn("h-4 w-4 text-muted-foreground flex-shrink-0", formattedLocation.isResolved && "text-blue-500")} />
                  <span className="truncate  text-sm">{formattedLocation.formattedAddress}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{formattedLocation.formattedAddress}</p>
              </TooltipContent>
            </Tooltip>
          </AddressAutoComplete>
        )}
      </DashboardCard.Content>
    </DashboardCard.Root>
  );
};

interface IAddressAutoCompleteProps extends IUseSwivuTaskLocationReturn {
  children: ReactNode;
  className?: string;
  clearable?: boolean;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

const AddressAutoComplete = ({
  formattedLocation,
  setUnresolvedLocation,
  onSearchAddress,
  setResolvedLocation,
  clearSearchResults,
  searchResults,
  isLoading,
  children,
  className,
  clearable = true,
  isOpen: externalIsOpen,
  onOpenChange: externalOnOpenChange
}: IAddressAutoCompleteProps) => {
  const [searchValue, setSearchValue] = useState<string>("");
  const [internalIsOpen, setInternalIsOpen] = useState<boolean>(false);
  const { formattedAddress } = formattedLocation;

  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const setIsOpen = externalOnOpenChange !== undefined ? externalOnOpenChange : setInternalIsOpen;

  const hasLocation = formattedAddress && formattedAddress.trim() !== "";

  const handleSearchChange = (value: string) => {
    setUnresolvedLocation(value);
    setSearchValue(value);
    onSearchAddress(value);
  };

  const handleLocationSelect = useCallback(
    (locationId: string) => {
      setResolvedLocation(locationId);
      setSearchValue("");
      setIsOpen(false);
    },
    [setResolvedLocation, setSearchValue, setIsOpen]
  );

  const handleClearLocation = useCallback(() => {
    setUnresolvedLocation("");
    setSearchValue("");
    setIsOpen(false);
  }, [setUnresolvedLocation, setSearchValue, setIsOpen]);

  return (
    <>
      <Popover
        open={isOpen}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            clearSearchResults();
          } else {
            setSearchValue(formattedAddress);
            onSearchAddress(formattedAddress);
          }
          setIsOpen(isOpen);
        }}
      >
        <div className={cn(`flex items-center gap-2 w-full`, className)}>
          <PopoverTrigger className="flex-1 min-w-0">{children}</PopoverTrigger>
          {formattedAddress && clearable && (
            <button onClick={handleClearLocation} className="flex-none p-1 hover:bg-muted rounded-sm transition-colors" title="Clear location">
              <X className="h-3 w-3 text-muted-foreground" />
            </button>
          )}
        </div>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start" alignOffset={-10}>
          <Command>
            <CommandInput value={searchValue} onValueChange={handleSearchChange} placeholder={hasLocation ? "Search for a different location..." : "Search for a location..."} />
            <CommandList>
              {isLoading && (
                <div className="flex items-center justify-center p-4">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-sm text-muted-foreground">Searching...</span>
                </div>
              )}
              {!isLoading && <CommandEmpty>{searchValue.trim().length > 0 ? "No locations found." : "Start typing to search for locations..."}</CommandEmpty>}
              {searchResults.length > 0 && (
                <CommandGroup heading="Locations">
                  {searchResults.map((result) => (
                    <CommandItem key={result.value} value={result.label} onSelect={() => handleLocationSelect(result.value)} className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span className="truncate">{result.label}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </>
  );
};

const AddressMenu = ({ formattedLocation }: { formattedLocation: IFormattedLocation }) => {
  const { record } = useEntityManagerContext();

  const menuItems = LOCATION_GEO_BUTTON_CONFIG.map(({ title, icon, url }) => {
    return {
      itemVariant: "button" as const,
      menuText: title,
      dropdownMenuItemProps: {
        asChild: true
      },
      Component: () => (
        <CommandItem key={title} className="flex items-center gap-2" asChild>
          <a
            href={geoLocationButtonUrlHelper(url, {
              lat: formattedLocation.lat,
              lon: formattedLocation.lon
            })}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 w-full"
          >
            <img src={icon} className="h-4 w-4" /> <span>{title}</span> <ArrowRight className="h-4 w-4 -rotate-45 ml-auto" />
          </a>
        </CommandItem>
      )
    };
  });
  return (
    <ButtonGroup>
      <MoreMenu menuItems={menuItems} triggerButtonVariant="entityHeader" triggerButtonSize="icon" record={record} disabled={!formattedLocation.isResolved} />
    </ButtonGroup>
  );
};
